/**
 * Utilidades de rol (frontend).
 *
 * Modelo de roles (ver CLAUDE.md §3.3):
 * Un usuario tiene EXACTAMENTE UN rol — string escalar, no array.
 * Roles válidos: `superadministrador` | `administrador` | `aseador` | `cliente`.
 *
 * Los helpers aquí aceptan defensivamente tanto string como `string[]`
 * porque puede haber tokens JWT viejos o `localStorage` con formato
 * array legacy. Siempre que leas `user.rol`, usa `normalizeRole()` o
 * `isAdminRole()` en lugar de comparar string directamente.
 *
 * ╔════════════════════════════════════════════════════════════════════╗
 * ║  ⚠️  ANTIPATRÓN PROHIBIDO — no reintroducir nunca:                 ║
 * ║                                                                    ║
 * ║      const roles = Array.isArray(user?.rol) ? user.rol : [];       ║
 * ║      const isAdmin = isAdminRole(roles);  // ❌ SIEMPRE false      ║
 * ║                                                                    ║
 * ║  Razón: tras el refactor 2026-04, `user.rol` es string escalar.    ║
 * ║  El `Array.isArray(...) ? ... : []` devuelve `[]` para un string,  ║
 * ║  y `isAdminRole([])` se normaliza a "cliente" → false. Los admins  ║
 * ║  quedaron invisibles (vieron vista cliente) durante varios días    ║
 * ║  en /calendario y /contratos antes de detectarlo.                  ║
 * ║                                                                    ║
 * ║  CORRECTO:                                                         ║
 * ║      const isAdmin = isAdminRole(user?.rol);     // acepta string  ║
 * ║      const rolExacto = normalizeRole(user?.rol); // "administrador"║
 * ║                                                                    ║
 * ║  NO hagas `user?.rol?.[0]` ni splits manuales — `normalizeRole`    ║
 * ║  ya resuelve arrays legacy, valores vacíos y mapeos heredados      ║
 * ║  ("propietario"/"usuario"/"otro" → "cliente") en un solo lugar.    ║
 * ╚════════════════════════════════════════════════════════════════════╝
 */
export type UserRole =
  | "superadministrador"
  | "administrador"
  | "aseador"
  | "cliente";

/**
 * Normaliza un valor de rol (string escalar, array legacy, null, etc.) al
 * string de rol efectivo. Elige el rol de mayor privilegio si vienen
 * varios. Maps legacy "propietario"/"usuario"/"otro" → "cliente".
 */
export function normalizeRole(
  value: string | string[] | undefined | null,
): UserRole {
  const mapLegacy = (r: unknown): string => {
    const k = (typeof r === "string" ? r : String(r ?? "")).trim().toLowerCase();
    if (!k) return "cliente";
    if (k === "propietario" || k === "usuario" || k === "otro") return "cliente";
    return k;
  };
  const candidates = Array.isArray(value)
    ? value.map(mapLegacy)
    : [mapLegacy(value)];
  const priority: UserRole[] = [
    "superadministrador",
    "administrador",
    "aseador",
    "cliente",
  ];
  for (const r of priority) {
    if (candidates.includes(r)) return r;
  }
  return "cliente";
}

/**
 * ¿El usuario tiene privilegios de administrador o superior?
 * Respeta la jerarquía: `superadministrador` también es admin.
 * Acepta string escalar o array legacy — usar SIEMPRE en vez de comparar
 * manualmente.
 */
export const isAdminRole = (
  rol: string | string[] | undefined | null,
): boolean => {
  const r = normalizeRole(rol);
  return r === "administrador" || r === "superadministrador";
};

/**
 * Qué puede hacer QUIEN MIRA con la fila de un usuario administrativo (administrador o superadministrador) en
 * Configuración → Administradores (round 2026-10-30). Regla de docs/auth-roles.md §4: SOLO el superadministrador edita o
 * elimina a un administrador; un administrador común no puede tocar a ninguno de los dos roles.
 *
 * Se decide acá, con el rol de quien mira y el de la fila, y NO se deja que el servidor rechace: antes el botón «Editar» salía
 * en las filas de superadministrador y guardar respondía 403 «No se puede modificar un superadministrador».
 *
 * Round 2026-10-31 (decisión del dueño: «sí, eso puede hacer un superadm»; el servidor ya lo acepta en
 * `PUT /api/admin/usuarios/:id`): un superadministrador SÍ edita a OTRO superadministrador —nombre, correo y teléfono; el rol nunca
 * se cambia— y así el par de cuentas que comparten teléfono se corrige desde Configuración. Lo que sigue sin ofrecerse:
 *   · Eliminar a un superadministrador (`DELETE` sigue prohibido).
 *   · Editar la PROPIA fila: cambiar el correo propio exige la contraseña actual y ese flujo vive en «Mi perfil».
 * Si el servidor dejara de aceptarlo, basta volver esta constante a `false`.
 */
export const SUPERADMIN_EDITA_SUPERADMIN_DESDE_CONFIGURACION = true;

export interface PermisosSobreUsuarioAdministrativo {
  puedeEditar: boolean;
  puedeEliminar: boolean;
  /** Por qué no se puede editar, para mostrarlo en el botón deshabilitado (solo si la fila es de un superadministrador). */
  motivoNoEditar?: string;
}

export interface OpcionesPermisosAdministrativos {
  /** La fila es la cuenta de quien mira: su correo propio pide la contraseña actual, así que se cambia desde Mi perfil. */
  esLaPropiaCuenta?: boolean;
}

export function permisosSobreUsuarioAdministrativo(
  rolQuienMira: string | string[] | undefined | null,
  rolFila: string | string[] | undefined | null,
  opciones: OpcionesPermisosAdministrativos = {},
): PermisosSobreUsuarioAdministrativo {
  const mira = normalizeRole(rolQuienMira);
  const fila = normalizeRole(rolFila);
  const sinPermiso: PermisosSobreUsuarioAdministrativo = { puedeEditar: false, puedeEliminar: false };

  // Solo el superadministrador gestiona a administradores y superadministradores.
  if (mira !== "superadministrador") return sinPermiso;

  if (fila === "administrador") return { puedeEditar: true, puedeEliminar: true };

  if (fila === "superadministrador") {
    // La propia cuenta se cambia desde Mi perfil (ahí se pide la contraseña actual al cambiar el correo).
    if (opciones.esLaPropiaCuenta) {
      return {
        puedeEditar: false,
        puedeEliminar: false,
        motivoNoEditar: "Tu propia cuenta (nombre, correo y teléfono) se cambia desde Mi perfil.",
      };
    }
    return SUPERADMIN_EDITA_SUPERADMIN_DESDE_CONFIGURACION
      ? { puedeEditar: true, puedeEliminar: false }
      : {
          puedeEditar: false,
          puedeEliminar: false,
          motivoNoEditar: "Cada superadministrador cambia sus datos y su teléfono desde Mi perfil.",
        };
  }

  // Esta lista es de administradores: cualquier otro rol se gestiona en su propia sección.
  return sinPermiso;
}
